import { useEffect, useRef, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { ParallaxBanner } from 'react-scroll-parallax';
import * as FaIcons from 'react-icons/fa';
import type { IconType } from 'react-icons';

import { AppLayout, ContactForm } from '../../../components';
import { ScrollToTop } from '../../../components/sections/ScrollToTop';
import { CompanyInfo } from '../../../components/sections/CompanyInfo';
import { RenderServicePlan } from '../../../components/sections/RenderServicePlan';
import { RenderTestimonial } from '../../../components/sections/RenderTestimonial';
import { InfiniteCarousel } from '../../../components/sections/InfiniteCarousel';
import { ClientsGlobe } from '../../../components/sections/ClientsGlobe';

const HomeBackground = require('../../../assets/images/home.png');
const djFlava = require('../../../assets/images/flava.png');
const sourcFromSport = require('../../../assets/images/source_from_sport_laptop.png');
const HpTagsAndLabels = require('../../../assets/images/HP.png');
const Cook4Life = require('../../../assets/images/cook4life.png');
const xencode = require('../../../assets/images/xenlaptop.png');
const GCC = require('../../../assets/images/GCC.png');
const gigabite = require('../../../assets/images/SFC.png');
const briefcase = require('../../../assets/images/about-circle.png');
const hands = require('../../../assets/images/contact-circle.png');
const getinTouch = require('../../../assets/images/get-in-touch.png');
const customers = require('../../../assets/images/customers.png');

const projects = [
  { title: 'DJ Flava', link: 'https://www.djflavainternational.com/home', image: djFlava },
  { title: 'Gigabite', link: 'https://gigabite-web.herokuapp.com', image: gigabite },
  {
    title: 'Source From Sport',
    link: 'https://www.sourcefromsport.com/home',
    image: sourcFromSport,
  },
  { title: 'Xencode', link: 'https://www.xencode.co.za', image: xencode },
  {
    title: 'Gratitude City Church',
    link: 'https://www.gratitudecitychurch.co.za/',
    image: GCC,
  },
  {
    title: 'HP Tags and Labels',
    link: 'https://www.hptagsandlabels.co.za/',
    image: HpTagsAndLabels,
  },
  {
    title: 'Cook4Life',
    link: 'https://www.cook4life.co.za',
    image: Cook4Life,
  },
];

const groupedStackIcons: {
  category: string;
  icons: { name: string; icon: IconType }[];
}[] = [
  {
    category: 'Frontend',
    icons: [
      { name: 'ReactJS / React Native', icon: FaIcons.FaReact },
      { name: 'JavaScript', icon: FaIcons.FaJsSquare },
      { name: 'TypeScript', icon: FaIcons.FaJsSquare },
      { name: 'CSS', icon: FaIcons.FaCss3Alt },
      { name: 'Shopify', icon: FaIcons.FaShopify },
      { name: 'WordPress', icon: FaIcons.FaWordpress },
    ],
  },
  {
    category: 'Backend',
    icons: [
      { name: 'NodeJS', icon: FaIcons.FaNodeJs },
      { name: 'PHP', icon: FaIcons.FaPhp },
      { name: 'Python', icon: FaIcons.FaPython },
      { name: 'Ruby on Rails', icon: FaIcons.FaGem },
      { name: 'C#', icon: FaIcons.FaCode },
      { name: 'ASP.NET', icon: FaIcons.FaCogs },
      { name: 'PyCaret', icon: FaIcons.FaRobot },
    ],
  },
  {
    category: 'Servers / DevOps',
    icons: [
      { name: 'Heroku', icon: FaIcons.FaCloudversify },
      { name: 'AWS', icon: FaIcons.FaAws },
      { name: 'Azure', icon: FaIcons.FaMicrosoft },
    ],
  },
  {
    category: 'Mobile Platforms',
    icons: [
      { name: 'Google Play Store', icon: FaIcons.FaGooglePlay },
      { name: 'TestFlight / iOS Store', icon: FaIcons.FaAppStoreIos },
    ],
  },
];

export const HomeScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const getQuoteButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    AOS.init({ duration: 1000, easing: 'ease-in-out', once: false });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const button = getQuoteButtonRef.current;
      const services = document.getElementById('services');
      const home = document.getElementById('home');
      if (!button || !services || !home) return;

      const scrollTop = window.scrollY;
      const servicesTop = services.offsetTop;
      const homeTop = home.offsetTop;

      if (window.innerWidth >= 768 && scrollTop >= homeTop && scrollTop <= servicesTop) {
        button.style.top = `${scrollTop - homeTop}px`;
      } else {
        button.style.top = '';
      }

      if (window.innerWidth < 768 && scrollTop >= homeTop) {
        button.style.position = 'fixed';
        button.style.bottom = '60px';
      } else {
        button.style.position = '';
        button.style.bottom = '';
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactMeClick = () => {
    scrollTo('contact');
  };

  return (
    <AppLayout toggle="home">
      <ScrollToTop />

      <section
        id="home"
        className="relative bg-xencode-black text-white overflow-hidden px-4 sm:px-6 md:px-8 py-24 sm:py-32 md:py-48"
      >
        <img
          src={HomeBackground}
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover opacity-20 z-0"
        />
        <div className="absolute top-0 left-0 w-60 sm:w-72 h-60 sm:h-72 bg-xencode-gold opacity-10 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-60 sm:w-72 h-60 sm:h-72 bg-xencode-gold opacity-10 blur-3xl rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto flex flex-col items-center md:items-start text-center md:text-left">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-aos="fade-down">
            WELCOME TO <span className="text-xencode-gold font-abeakrg">XENCODE</span>
          </h2>

          <p
            className="mt-4 sm:mt-6 text-sm sm:text-base font-light text-gray-200 tracking-wider max-w-md sm:max-w-xl px-2 sm:px-0"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            Your brand’s window to the world. We empower startups and growing businesses with
            exceptional websites, mobile apps, and eCommerce platforms — designed to stand out and
            scale.
          </p>
          <div className="mt-8 sm:mt-10" data-aos="fade-up" data-aos-delay="300">
            <button
              ref={getQuoteButtonRef}
              disabled={isLoading}
              onClick={() => scrollTo('contact')}
              className="bg-xencode-contact-black text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-xencode-gold hover:text-black transition shadow-lg"
            >
              Get a Quote
            </button>
          </div>
        </div>
      </section>

      {/* Clients + Interactive Globe */}
      <ParallaxBanner
        layers={[
          {
            speed: -20,
            children: <div className="absolute inset-0 bg-gradient-to-br" />,
          },
        ]}
        className="relative min-h-screen text-white overflow-hidden flex items-center justify-center py-32 bg-black border-b-2"
      >
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-8 text-center space-y-10">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight" data-aos="fade-up">
            Our <span className="text-xencode-gold font-abeakrg">Global Clients</span>
          </h2>

          <p
            className="text-sm md:text-base text-white max-w-xl mx-auto"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            We’ve partnered with companies across the globe — explore our client reach interactively
            on the 3D map below.
          </p>

          <div className="relative w-full overflow-hidden">
            {/* Globe */}
            <div className="relative w-full h-full">
              <ClientsGlobe />
            </div>
          </div>
        </div>
      </ParallaxBanner>

      {/* About + Benefits Section */}
      <section
        id="about"
        className="relative px-4 md:px-8 py-24 bg-gradient-to-br from-xencode-black via-[#0f0f0f] to-xencode-contact-black text-white overflow-hidden"
      >
        {/* Glow Effects */}
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-xencode-gold opacity-10 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-xencode-gold opacity-10 blur-3xl rounded-full pointer-events-none" />

        {/* About Text */}
        <div className="relative z-10 max-w-6xl mx-auto text-center md:text-left mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" data-aos="fade-right">
            About <span className="text-xencode-gold font-abeakrg">Us</span>
          </h2>
          <p
            className="text-sm md:text-base text-gray-300 tracking-wide max-w-2xl"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Xencode helps your business reach its full potential through premium websites, mobile
            platforms, and digital solutions. We combine creative design, clean code, and strategic
            thinking to elevate your brand’s online presence.
          </p>
        </div>

        {/* Benefits */}
        <CompanyInfo
          image={briefcase}
          title="A Website Makes You Look Professional"
          text="Nearly 90% of consumers trust businesses with a professional site. Your website builds trust, shows your expertise, and displays credentials customers look for."
        />
        <CompanyInfo
          image={hands}
          title="Visually Showcase Your Services"
          text="Before they reach out, customers want to see what you offer. Your website becomes the perfect gallery for your products, team, and story — first impressions matter."
        />
        <CompanyInfo
          image={customers}
          title="Encourage More Customer Contact"
          text="We place your contact info everywhere it matters — headers, footers, and forms. Easy access = more engagement. And we guard your email from spam, too."
        />
      </section>

      {/* Developer Stack Section */}
      <section className="relative px-4 md:px-8 py-24 bg-white text-center">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-xencode-black"
            data-aos="fade-up"
          >
            Technologies We <span className="text-xencode-gold font-abeakrg">Use</span>
          </h2>
          <p
            className="text-sm md:text-base text-gray-700 max-w-2xl mx-auto mb-12"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            We work with a wide range of modern technologies, frameworks, and platforms to deliver
            powerful, scalable, and future-ready solutions.
          </p>

          <div className="space-y-16">
            {groupedStackIcons.map(({ category, icons }, i) => (
              <div key={category} data-aos="fade-up" data-aos-delay={200 + i * 100}>
                <h3 className="text-2xl font-bold text-xencode-gold mb-6">{category}</h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-10 justify-items-center">
                  {icons.map(({ name, icon }) => {
                    const IconComponent = icon as unknown as (props: {
                      className?: string;
                    }) => JSX.Element;
                    return (
                      <div
                        key={name}
                        className="group flex flex-col items-center space-y-2 transition-transform transform hover:scale-110 hover:text-xencode-gold duration-300 ease-in-out"
                      >
                        <div className="p-4 rounded-full bg-gray-100 shadow-md group-hover:shadow-xl transition duration-300 ease-in-out">
                          <IconComponent className="w-10 h-10 text-xencode-black group-hover:text-xencode-gold transition-colors duration-300" />
                        </div>
                        <span className="text-sm text-gray-700 font-semibold text-center group-hover:text-xencode-black">
                          {name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-business bg-fixed bg-cover bg-center py-28 md:py-40 z-20 overflow-hidden">
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-black/50 z-0" />

        {/* CTA Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 text-white text-center md:text-left">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" data-aos="fade-up">
            CONNECT WITH <span className="text-xencode-gold font-abeakrg">US</span>
          </h2>
          <p
            className="text-sm md:text-base text-gray-200 tracking-wide max-w-xl mb-8"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            Let us know which plan you’re interested in, and we’ll get in touch with tailored advice
            and options for your project.
          </p>

          <div className="text-center md:text-left" data-aos="fade-up" data-aos-delay="300">
            <button
              onClick={handleContactMeClick}
              className="bg-xencode-gold text-black px-8 py-3 rounded-full text-sm font-semibold hover:bg-xencode-black hover:text-white transition shadow-lg"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <section
        id="services"
        className="relative px-4 md:px-8 py-20 bg-gradient-to-br from-xencode-black via-[#0f0f0f] to-xencode-contact-black text-white overflow-hidden pb-52"
      >
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-xencode-gold opacity-10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-xencode-gold opacity-10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto text-center md:text-left">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-aos="fade-down">
            Our <span className="text-xencode-gold font-abeakrg">Services</span>
          </h2>
          <p
            className="text-sm md:text-base text-gray-300 font-light tracking-wider max-w-xl"
            data-aos="fade-up"
          >
            Choose a plan that suits your business goals — and mention it in your contact form to
            help us respond better and faster.
          </p>
        </div>

        <div
          className="relative max-w-6xl mx-auto mt-16 grid gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
          data-aos="fade-up"
        >
          <RenderServicePlan
            title="PLAN 1"
            subtitle="Start-up and Business Design"
            details={[
              'Custom Front-end Development',
              'Free start Consultation',
              'Set up Server',
              'Maintenance 1 month',
            ]}
          />
          <RenderServicePlan
            title="PLAN 2"
            subtitle="Website Rebuild / Upgrade"
            details={[
              'Redesign Prototype',
              'Website Overhaul',
              'ReactJs/Javascript',
              'Maintenance 1 month',
            ]}
          />
          <RenderServicePlan
            title="PLAN 3"
            subtitle="Ecommerce"
            details={[
              'Custom Front-end Development',
              'Custom Back-end Development',
              'Architectural Diagram',
              'Design Prototypes',
              'Database Setup',
              'Add Payment Gateway',
              'Set up Server',
              'Free Consultation',
              'Maintenance for 2 months',
            ]}
          />
          <RenderServicePlan
            title="PLAN 4"
            subtitle="Mobile App"
            details={[
              'Custom Front-end Development',
              'Custom Back-end Development',
              'Architectural Diagram',
              'Design Prototypes',
              'Database Setup',
              'Add Payment Gateway',
              'Set up Server',
              'Free Consultation',
              'Maintenance for 2 months',
            ]}
          />
        </div>
      </section>

      {/* Projects Section */}
      <section
        id="projects"
        className="relative overflow-hidden bg-xencode-black py-24 px-4 md:px-8 md:rounded-t-[100px] -mt-24"
      >
        <div className="relative z-10 max-w-6xl mx-auto text-white text-center md:text-left">
          <h2
            className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4"
            data-aos="fade-up"
          >
            Our <span className="text-xencode-gold font-abeakrg">Projects</span>
          </h2>
          <p className="text-sm md:text-base text-gray-300 tracking-wide max-w-2xl">
            Explore some of the digital platforms and brands we’ve built — each tailored to meet
            unique business needs.
          </p>
        </div>

        <div className="relative z-10 mt-12" data-aos="fade-up" data-aos-delay="300">
          <InfiniteCarousel items={projects} />
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-2 py-20 bg-xencode-gold">
        <div className="max-w-6xl mx-auto text-white text-center md:text-left">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" data-aos="fade-right">
            Our <span className="text-black font-abeakrg">Testimonials</span>
          </h2>

          <p className="text-sm md:text-base tracking-wider">Here's what our clients say</p>
        </div>
        <div className="max-w-6xl mx-auto md:grid grid-cols-1 md:grid-cols-2 gap-3">
          <RenderTestimonial
            client="Danielle"
            details="Working with Xencode to create my website was incredible. They exceeded expectations and delivered a standout result."
          />
          <RenderTestimonial
            client="Flava"
            details="I approached Xencode to build my business website. Their service was exceptional, and I’d recommend them to anyone!"
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-12 bg-xencode-gold w-full text-center text-white">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl mb-2">A website is your brand's window to the world!</h1>
          <h1 className="text-3xl mb-2">
            <span className="text-xencode-black font-abeakrg mr-2">Xencode</span> is here to build
            that window.
          </h1>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="w-full px-4 md:px-0 py-16 bg-gradient-to-br from-xencode-black via-[#0f0f0f] to-xencode-contact-black relative overflow-hidden"
      >
        {/* Gold glow accents */}
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-xencode-gold opacity-20 blur-3xl rounded-full pointer-events-none animate-pulse" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-xencode-gold opacity-10 blur-3xl rounded-full pointer-events-none animate-pulse" />

        <div className="max-w-6xl mx-auto text-white md:flex md:items-center gap-10 relative z-10">
          {/* Visual */}
          <div className="w-full flex justify-center md:justify-start mb-10 md:mb-0">
            <img src={getinTouch} alt="Get In Touch" className="w-full h-auto object-contain" />
          </div>

          {/* Form */}
          <div className="w-full">
            <h2 className="text-2xl font-bold text-xencode-gold mb-4 tracking-widest uppercase">
              Let’s Get In Touch
            </h2>
            <p className="text-sm text-gray-300 mb-6 tracking-wide leading-relaxed">
              Fill in the form and we’ll respond as soon as possible. Whether it’s a new project or
              a simple question — we’d love to hear from you.
            </p>
            <ContactForm />
          </div>
        </div>
      </section>
    </AppLayout>
  );
};
